import axios from "axios";
import { store } from "../store/store";
import { refreshToken, logoutAdmin } from "../store/slices/authSlice";

// Use relative path in development to leverage Vite proxy (avoids CORS)
// In production, use the full URL from env or default
const API_URL = import.meta.env.DEV
  ? "/api" // Relative path uses Vite proxy in development
  : import.meta.env.VITE_API_BASE_URL_PUBLIC || "https://api.prokopi.in/api";

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshTokenValue = localStorage.getItem("refreshToken");

      if (!refreshTokenValue) {
        // No refresh token, logout user
        processQueue(error, null);
        isRefreshing = false;
        store.dispatch(logoutAdmin());
        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, {
          refreshToken: refreshTokenValue,
        });

        const { accessToken, user } = response.data;
        const newToken = accessToken || response.data.token;

        if (newToken) {
          // Update stored tokens
          localStorage.setItem("token", newToken);
          if (user) {
            localStorage.setItem("user", JSON.stringify(user));
          }

          // Update token expiration
          const tokenExpiry = Date.now() + 24 * 60 * 60 * 1000;
          localStorage.setItem("tokenExpiry", tokenExpiry.toString());

          // Update store
          store.dispatch({
            type: "auth/setAuth",
            payload: {
              token: newToken,
              user: user || JSON.parse(localStorage.getItem("user") || "{}"),
            },
          });

          // Update original request header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Process queued requests
          processQueue(null, newToken);
          isRefreshing = false;

          // Retry original request
          return api(originalRequest);
        } else {
          throw new Error("No token in refresh response");
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        processQueue(refreshError, null);
        isRefreshing = false;
        store.dispatch(logoutAdmin());
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    // For other errors, just reject
    return Promise.reject(error);
  }
);

export default api;
